import { useEffect, useLayoutEffect, useState, type ReactElement } from 'react';
import { readBootstrap } from '../../types/bootstrap';
import { ApiError, apiFetch } from '../auth/api-client';
import { buildSmsHref } from '../../../../shared-web/onboarding-phone';
import { useAsyncAction } from '../../../../shared-web/useAsyncAction';
import { OpenMessagesCta } from '../_shared/OpenMessagesCta';
import './LegalReviewPage.css';

interface AcceptResponse {
  status: 'accepted';
  version: string;
}

function errorCodeOf(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'error' in (err.body as Record<string, unknown>)) {
    return String((err.body as { error: unknown }).error);
  }
  return '';
}

/**
 * Re-consent surface served at `/legal/review`. Reached via the
 * tos-outdated inbound gate's identified link — the redirect handler
 * already minted a session before this page rendered, so all we need
 * to do is collect a single "I agree" tap and POST it.
 *
 * UI shape per Riley's spec: Terms + Privacy share ONE accepted value,
 * so the page shows one checkbox and one button regardless of how many
 * docs actually changed. Only the docs flagged in `docsUpdated` get
 * surfaced for review — no point asking the user to re-read Privacy
 * when the change was Terms-only.
 */
export function LegalReviewPage(): ReactElement {
  const boot = readBootstrap();
  if (boot.route !== 'legal-review') {
    return <div className="legal-review-error">Unexpected route</div>;
  }

  const currentVersion = boot.currentVersion || '';
  const requestedVersion = boot.requestedVersion || '';
  // If the link was minted at an older CURRENT_LEGAL_VERSION than what
  // the server thinks is current right now (rare — only if we bumped
  // again while the link was sitting in iMessage), block the accept
  // until the user reloads. Accepting the older version is meaningless
  // because the gate would just re-fire on their next inbound.
  const versionDrift = Boolean(requestedVersion) && requestedVersion !== currentVersion;

  const docsUpdated = boot.docsUpdated || {};
  const versionNotes = boot.versionNotes || '';

  const showTerms = docsUpdated.terms !== false; // default-true if missing
  const showPrivacy = docsUpdated.privacy !== false;

  // Where "you're all set" leads. Two arrivals, two answers, and the web one
  // wins when both are present: someone sent here from a page in the app is
  // waiting to get back to that page, and an sms: link would drop them into
  // Messages instead. `returnTo` is server-sanitized to a same-origin path.
  //
  // sms: deep link straight back to the gated thread (empty body → just
  // reopen the conversation, no prefilled text). Empty when the server
  // couldn't resolve the user's Ember number.
  const oriNumber = boot.oriNumber || '';
  const returnTo = boot.returnTo || '';
  const smsHref = oriNumber ? buildSmsHref(oriNumber, '') : '';
  const returnHref = returnTo || smsHref;
  const returnsToApp = Boolean(returnTo);
  const previewDone = import.meta.env.DEV
    && new URLSearchParams(window.location.search).get('preview') === 'done';

  // This standalone route lives outside EmberApp, so opt it into the same
  // desktop-width body contract while it is mounted. The page CSS carries the
  // Request tokens locally; this class only removes the phone-shell body cap.
  useLayoutEffect(() => {
    document.body.classList.add('legal-review-body');
    return () => document.body.classList.remove('legal-review-body');
  }, []);

  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(previewDone);

  // After acceptance, bounce back to Messages automatically (~2s) so the
  // user lands in their thread without a manual tap. The button below is
  // the fallback for when the auto-redirect is blocked or there's no
  // number to return to.
  useEffect(() => {
    if (!done || !returnHref || previewDone) return undefined;
    const t = setTimeout(() => {
      window.location.href = returnHref;
    }, 2000);
    return () => clearTimeout(t);
  }, [done, returnHref, previewDone]);

  const { run: handleSubmit, pending: submitting } = useAsyncAction(async () => {
    if (!agreed || versionDrift) return;
    setError(null);
    try {
      await apiFetch<AcceptResponse>('/api/legal/accept', {
        method: 'POST',
        body: { version: currentVersion, oriNumber },
      });
      setDone(true);
    } catch (err) {
      const code = errorCodeOf(err);
      if (code === 'version_outdated') {
        setError('A newer version was just published. Please refresh the page.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Your link expired. Please open the link from your iMessage again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  });

  if (done) {
    return (
      <div className="legal-review-shell">
        <div className="legal-review-card is-complete">
          <div className="legal-review-complete-mark" aria-hidden>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5 12 4 4L19 6" />
            </svg>
          </div>
          <div className="legal-review-eyebrow">Review complete</div>
          <h1 className="legal-review-title">You&rsquo;re all set</h1>
          <p className="legal-review-body">
            {returnsToApp
              ? 'Taking you back to where you left off.'
              : returnHref
                ? 'Taking you back to your iMessage thread with Ember — we’ll pick up where you left off.'
                : 'Head back to your iMessage thread with Ember — we’ll pick up where you left off.'}
          </p>
          {returnsToApp ? (
            <a className="legal-review-submit" href={returnHref}>Continue</a>
          ) : returnHref ? (
            <OpenMessagesCta
              href={returnHref}
              subtitle="Back to your chat with Ember"
              className="legal-review-open-messages"
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="legal-review-shell">
      <div className="legal-review-card">
        <div className="legal-review-eyebrow">A quick update</div>
        <h1 className="legal-review-title">We&rsquo;ve updated our terms</h1>
        <p className="legal-review-body">
          Quick review and we&rsquo;re back on it. Take a minute to look over what changed, then tap below.
        </p>

        {versionNotes ? (
          <div className="legal-review-notes">
            <div className="legal-review-notes-label">What changed</div>
            <div className="legal-review-notes-body">{versionNotes}</div>
          </div>
        ) : null}

        <div className="legal-review-docs">
          {showTerms ? (
            <a className="legal-review-doc" href="/terms" target="_blank" rel="noreferrer">
              <span className="legal-review-doc-name">Terms of Service</span>
              <span className="legal-review-doc-cta">View &rarr;</span>
            </a>
          ) : null}
          {showPrivacy ? (
            <a className="legal-review-doc" href="/privacy" target="_blank" rel="noreferrer">
              <span className="legal-review-doc-name">Privacy Policy</span>
              <span className="legal-review-doc-cta">View &rarr;</span>
            </a>
          ) : null}
        </div>

        <label className="legal-review-checkbox">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting || versionDrift}
          />
          <span>I&rsquo;ve read and agree to the updated terms.</span>
        </label>

        {versionDrift ? (
          <div className="legal-review-banner">
            A newer version is available. Please refresh this page to see it.
          </div>
        ) : null}
        {error ? <div className="legal-review-banner">{error}</div> : null}

        <button
          type="button"
          className="legal-review-submit"
          onClick={handleSubmit}
          disabled={!agreed || submitting || versionDrift}
        >
          {submitting ? 'Saving…' : 'Agree and continue'}
        </button>

        <div className="legal-review-version">Version {currentVersion}</div>
      </div>
    </div>
  );
}
